import React, { useState, useRef, useEffect } from "react";
import { Order } from "../../services/orderService";
import { FilterOrdersRequest } from "../../services/admin/orderService";
import ConfirmModal from "./ConfirmModal";
import Pagination from "../common/Pagination";

interface OrderListProps {
  orders: Order[];
  loading: boolean;
  filters: FilterOrdersRequest;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onFilterChange: (filters: Partial<FilterOrdersRequest>) => void;
  onPageChange: (page: number) => void;
  onOrderSelect: (order: Order) => void;
  onStatusUpdate: () => void;
  onQuickStatusUpdate?: (orderId: string, status: string) => Promise<void>;
}

const OrderList: React.FC<OrderListProps> = ({
  orders,
  loading,
  filters,
  pagination,
  onFilterChange,
  onPageChange,
  onOrderSelect,
  onStatusUpdate,
  onQuickStatusUpdate,
}) => {
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState(filters.search || "");
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    orderId: string;
    status: string;
  }>({ isOpen: false, orderId: "", status: "" });
  const [notPickingUpOrders, setNotPickingUpOrders] = useState<Set<string>>(new Set());

  // Sync search value with filters
  useEffect(() => {
    setSearchValue(filters.search || "");
  }, [filters.search]);

  // Clear not picking up status only for orders that are shipped (completed)
  useEffect(() => {
    setNotPickingUpOrders((prev) => {
      const newSet = new Set(prev);
      orders.forEach((order) => {
        if (order.status === "shipped") {
          newSet.delete(order.id);
        }
      });
      return newSet;
    });
  }, [orders]);

  // Debounced search to prevent too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== (filters.search || "")) {
        onFilterChange({ search: searchValue.trim() || undefined });
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleQuickStatusUpdate = async (orderId: string, status: string) => {
    if (!onQuickStatusUpdate) {
      console.error("onQuickStatusUpdate is not provided");
      return;
    }
    
    setUpdatingOrderId(orderId);
    try {
      await onQuickStatusUpdate(orderId, status);
      // Only remove from not picking up list if order is shipped (completed)
      // Keep the mark for canceled, confirmed, preparing, etc.
      if (status === "shipped") {
        setNotPickingUpOrders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
      }
      // Status update will trigger a reload via onStatusUpdate
    } catch (error: any) {
      console.error("Failed to update order status:", error);
      alert(error?.message || "فشل تحديث حالة الطلب");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleNotPickingUp = (orderId: string) => {
    setNotPickingUpOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        // If already marked, remove it (toggle off)
        newSet.delete(orderId);
      } else {
        // If not marked, add it (toggle on)
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleConfirmCancel = async () => {
    if (!onQuickStatusUpdate) return;
    
    const { orderId, status } = confirmModal;
    setConfirmModal({ isOpen: false, orderId: "", status: "" });
    
    setUpdatingOrderId(orderId);
    try {
      await onQuickStatusUpdate(orderId, status);
      // Status update will trigger a reload via onStatusUpdate
    } catch (error: any) {
      console.error("Failed to update order status:", error);
      alert(error?.message || "فشل تحديث حالة الطلب");
    } finally {
      setUpdatingOrderId(null);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "confirmed":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "preparing":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      case "shipped":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "canceled":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      new: "جديد",
      confirmed: "مؤكد",
      preparing: "قيد التحضير",
      shipped: "تم الشحن",
      canceled: "ملغي",
    };
    return labels[status] || status;
  };

  if (loading && orders.length === 0) {
    return <div className="text-center py-8">جاري التحميل...</div>;
  }

  return (
    <div>
      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search Bar */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              البحث
            </label>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="بحث..."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3 focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
            />
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              الحالة
            </label>
            <select
              value={filters.status || ""}
              onChange={(e) =>
                onFilterChange({ status: e.target.value || undefined })
              }
              className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
            >
              <option value="">الكل</option>
              <option value="new">جديد</option>
              <option value="confirmed">مؤكد</option>
              <option value="preparing">قيد التحضير</option>
              <option value="shipped">تم الشحن</option>
              <option value="canceled">ملغي</option>
            </select>
          </div>
          
          {/* Date Filters */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              من تاريخ
            </label>
            <input
              type="date"
              value={filters.startDate || ""}
              onChange={(e) =>
                onFilterChange({ startDate: e.target.value || undefined })
              }
              className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              إلى تاريخ
            </label>
            <input
              type="date"
              value={filters.endDate || ""}
              onChange={(e) =>
                onFilterChange({ endDate: e.target.value || undefined })
              }
              className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
            />
          </div>
        </div>
        
        {/* Reset Button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() =>
              onFilterChange({ status: undefined, startDate: undefined, endDate: undefined, search: undefined })
            }
            className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition text-sm"
          >
            إعادة تعيين
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  رقم الطلب
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  العميل
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  المنتج
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  السعر
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  التاريخ
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-[#0d171b] dark:text-white">
                  الإجراءات السريعة
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {orders.map((order) => {
                const isNotPickingUp = notPickingUpOrders.has(order.id);
                return (
                <tr
                  key={order.id}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors ${
                    isNotPickingUp ? "bg-red-100 dark:bg-red-900/30" : ""
                  }`}
                  onClick={() => onOrderSelect(order)}
                >
                  <td className="px-6 py-4 text-sm text-[#0d171b] dark:text-white font-medium">
                    <span dir="ltr" style={{ display: 'inline-block', textAlign: 'left' }}>{order.orderNumber}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#0d171b] dark:text-white">
                    <div>{order.customerName}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400" dir="ltr" style={{ textAlign: 'left' }}>
                      {order.customerPhone}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {order.product.name}
                    {order.variant && (
                      <div className="text-xs">
                        {order.variant.variantType}: {order.variant.variantValue}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#0d171b] dark:text-white">
                    {Number(order.finalPrice).toFixed(2)} د.ج
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded text-xs ${getStatusColor(
                        order.status
                      )}`}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {new Date(order.createdAt).toLocaleDateString("ar-DZ")}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      {order.status === "new" && (
                        <>
                          <button
                            onClick={async () => {
                              await handleQuickStatusUpdate(order.id, "confirmed");
                            }}
                            disabled={updatingOrderId === order.id}
                            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                          >
                            {updatingOrderId === order.id ? "..." : "قبول"}
                          </button>
                          <button
                            onClick={() => {
                              setConfirmModal({ isOpen: true, orderId: order.id, status: "canceled" });
                            }}
                            disabled={updatingOrderId === order.id}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                          >
                            {updatingOrderId === order.id ? "..." : "رفض"}
                          </button>
                        </>
                      )}
                      {order.status === "confirmed" && (
                        <button
                          onClick={async () => {
                            await handleQuickStatusUpdate(order.id, "new");
                          }}
                          disabled={updatingOrderId === order.id}
                          className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                        >
                          {updatingOrderId === order.id ? "..." : "إلغاء القبول"}
                        </button>
                      )}
                      {order.status === "canceled" && (
                        <button
                          onClick={async () => {
                            await handleQuickStatusUpdate(order.id, "new");
                          }}
                          disabled={updatingOrderId === order.id}
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                        >
                          {updatingOrderId === order.id ? "..." : "إلغاء الإلغاء"}
                        </button>
                      )}
                      {/* Client not picking up button - only show for new and preparing statuses */}
                      {(order.status === "new" || order.status === "preparing") && (
                        <button
                          onClick={() => handleNotPickingUp(order.id)}
                          className={`px-3 py-1 rounded hover:opacity-80 transition text-xs ${
                            isNotPickingUp
                              ? "bg-orange-600 text-white"
                              : "bg-orange-500 text-white"
                          }`}
                          title={isNotPickingUp ? "إلغاء التمييز" : "العميل لا يجيب"}
                        >
                          {isNotPickingUp ? "✓ لا يجيب" : "لا يجيب"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && !loading && (
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            لا توجد طلبات
          </div>
        )}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="تأكيد رفض الطلب"
        message="هل أنت متأكد من رفض هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء."
        confirmText="نعم، رفض الطلب"
        cancelText="إلغاء"
        variant="danger"
        onConfirm={handleConfirmCancel}
        onCancel={() => setConfirmModal({ isOpen: false, orderId: "", status: "" })}
      />
    </div>
  );
};

export default OrderList;

